import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { clearAuthSession } from "../helpers/authSession";
import Constant from "../util/constant";

/** Temporary for testing — adjust or remove before production. */
const IDLE_LOGOUT_MS = 5 * 60 * 1000;

const IDLE_LOGOUT_ROLES = new Set([
  Constant.ROLES.ADMIN,
  Constant.ROLES.CLUB_MANAGER,
  Constant.ROLES.CLUB_EMPLOYEE,
]);

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

/**
 * Logs out after no user activity for IDLE_LOGOUT_MS (admin, club manager, employee).
 */
export default function useIdleLogout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const lastResetRef = useRef(0);

  const logout = useCallback(async () => {
    console.warn(
      `[idle-logout] No activity for ${IDLE_LOGOUT_MS / 1000}s — signing out`
    );
    await clearAuthSession(dispatch);
    navigate("/", { replace: true });
  }, [dispatch, navigate]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
    }
    timerRef.current = window.setTimeout(logout, IDLE_LOGOUT_MS);
  }, [logout]);

  useEffect(() => {
    const role = window.localStorage.getItem("role");
    if (!role || !IDLE_LOGOUT_ROLES.has(role)) {
      return undefined;
    }

    const onActivity = () => {
      const now = Date.now();
      if (now - lastResetRef.current < 1000) {
        return;
      }
      lastResetRef.current = now;
      resetTimer();
    };

    resetTimer();
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, onActivity, { passive: true });
    });

    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, onActivity);
      });
    };
  }, [resetTimer]);
}
